<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50"
  >
    <div v-if="loading" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div class="spinner"></div>
    </div>
    <div
      class="bg-white p-8 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
      style="max-height: 90%"
    >
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-2xl font-bold text-gray-800">
          Survey Details: {{ survey.title }}
        </h2>
        <button
          @click="$emit('close')"
          class="text-gray-600 hover:text-gray-800 focus:outline-none"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </button>
      </div>
      <div class="mb-4">
        <p><strong>Survey Type:</strong> {{ survey.type }}</p>
        <p><strong>Status:</strong> {{ survey.status }}</p>
        <p>
          <strong>Default Start Date:</strong> {{ formatDate(survey.startDate)
          }}<strong> Default End Date:</strong> {{ formatDate(survey.endDate) }}
        </p>
      </div>
      <div class="mb-4">
        <button
          @click="toggleBatchUpdate"
          class="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition duration-300"
        >
          {{ showBatchUpdate ? "Hide" : "Show" }} Batch Update
        </button>
       
      </div>
      <div v-if="showBatchUpdate" class="mb-4">
        <h3 class="text-xl font-semibold mb-2">Batch Update Dates</h3>
        <div class="flex space-x-4 mb-2F">
          <div>
            <label class="block text-sm font-medium text-gray-700"
              >Batch Start Date</label
            >
            <input
              type="datetime-local"
              v-model="batchStartDate"
              :min="new Date().toISOString().slice(0, 16)"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700"
              >Batch End Date</label
            >
            <input
              type="datetime-local"
              v-model="batchEndDate"
              :min="new Date().toISOString().slice(0, 16)"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
          </div>
        </div>
        <div class="flex space-x-2">
          <button
            @click="applyBatchDates('all')"
            class="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-300"
          >
            Apply to All Modules
          </button>
          <button
            @click="applyBatchDates('selected')"
            class="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition duration-300"
          >
            Apply to Selected Modules
          </button>
        </div>
      </div>
      <div class="mb-4">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search modules..."
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      <div v-if="loading" class="flex justify-center items-center py-4">
        <div
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"
        ></div>
      </div>
      <div v-else-if="error" class="text-red-600 mb-4">
        {{ error }}
      </div>
      <div v-else class="mb-4 flex-grow overflow-y-auto">
        <h3 class="text-xl font-semibold mb-2">Module-specific Dates</h3>
        <div
          v-for="module in paginatedModules"
          :key="module.id"
          class="mb-4 p-4 border border-gray-200 rounded-md"
        >
          <div class="flex items-center mb-2">
            <input
              type="checkbox"
              :id="'module-' + module.id"
              v-model="selectedModules"
              :value="module.id"
              class="mr-2"
            />
            <label :for="'module-' + module.id" class="font-semibold"
              >{{ module.moduleCode }} - {{ module.name }}</label
            >
          </div>
          <div class="flex space-x-4">
            <div>
              <label class="block text-sm font-medium text-gray-700"
                >Start Date</label
              >
                <input
                type="datetime-local"
                :value="
                  formatDateForInput(
                  moduleDates[module.id]?.startDate || survey.startDate
                  )
                "
                @input="
                  updateModuleDate(module.id, 'startDate', $event.target.value)
                "
                :min="new Date().toISOString().slice(0, 16)"
                :max="survey.endDate"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700"
                >End Date</label
              >
              <input
                type="datetime-local"
                :value="
                  formatDateForInput(
                    moduleDates[module.id]?.endDate || survey.endDate
                  )
                "
                @input="
                  updateModuleDate(module.id, 'endDate', $event.target.value)
                "
                :min="new Date().toISOString().slice(0, 16)"
                :max="survey.endDate"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="flex justify-between items-center mt-4">
        <div class="flex space-x-2">
          <button
            @click="currentPage--"
            :disabled="currentPage === 1"
            class="px-3 py-1 bg-gray-200 text-gray-700 rounded-md disabled:opacity-50"
          >
            Previous
          </button>
          <button
            @click="currentPage++"
            :disabled="currentPage === totalPages"
            class="px-3 py-1 bg-gray-200 text-gray-700 rounded-md disabled:opacity-50"
          >
            Next
          </button>
        </div>
        <span class="text-sm text-gray-600">
          Page {{ currentPage }} of {{ totalPages }}
        </span>
      </div>
      <div class="flex justify-end mt-4" v-show="userRole=='admin' || survey.status == 'Published'"
      >
        <button
          @click="saveDates"
          class="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition duration-300"
        >
          Save Changes
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { surveyApi } from "@/api/surveyManagement";
import { reportsApi } from "@/api/reports";

export default {
  name: "SurveyDetailModal",
  props: {
    show: Boolean,
    survey: Object,
  },
  data() {
    return {
      moduleDates: {},
      changedModules: new Set(),
      currentPage: 1,
      itemsPerPage: 5,
      searchQuery: "",
      loading: false,
      error: null,
      batchStartDate: "",
      batchEndDate: "",
      selectedModules: [],
      showBatchUpdate: false,
    };
  },
  computed: {
    ...mapState({
      modules: (state) => state.modules,
      userRole: (state) => state.auth.userRole,
      userSchool: (state) => state.auth.userSchool,
      userDepartment: (state) => state.auth.userDepartment,
    }),
    filteredModules() {
      let _modules = this.modules.sort((a, b) => a.moduleCode.localeCompare(b.moduleCode));
      return _modules.filter((module) => {
        const matchesSearch =
          module.moduleCode
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase()) ||
          module.name.toLowerCase().includes(this.searchQuery.toLowerCase());
        if (this.userRole === "admin") {
          return matchesSearch;
        } else if (this.userRole === "school_admin") {
          return matchesSearch && module.school === this.userSchool;
        } else if (this.userRole === "department_admin") {
          return matchesSearch && module.department === this.userDepartment;
        }
        return false;
      });
    },
    totalPages() {
      return Math.ceil(this.filteredModules.length / this.itemsPerPage);
    },
    paginatedModules() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredModules.slice(start, end);
    },
  },
  methods: {
    ...mapActions("surveys", ["updateSurveyModuleDates"]),
    async fetchModuleDates() {
      this.loading = true;
      this.error = null;
      try {
        const dates = await surveyApi.getModuleDates(this.survey.id);
        this.moduleDates = dates.reduce((acc, date) => {
          acc[date.moduleId] = {
            startDate: date.startDate,
            endDate: date.endDate,
          };
          return acc;
        }, {});
      } catch (error) {
        console.error("Error fetching module dates:", error);
        this.error = "Failed to fetch module dates. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    updateModuleDate(moduleId, dateType, value) {
      if (!this.moduleDates[moduleId]) {
        this.moduleDates[moduleId] = {
          startDate: this.survey.startDate,
          endDate: this.survey.endDate,
        };
      }
      const newDate = new Date(value).toISOString();
      this.moduleDates[moduleId][dateType] = newDate;
      this.changedModules.add(moduleId);
    },
    applyBatchDates(type) {
      const modulesToUpdate =
        type === "all"
          ? this.modules
          : this.modules.filter((m) => this.selectedModules.includes(m.id));

      modulesToUpdate.forEach((module) => {
        if (this.batchStartDate) {
          this.updateModuleDate(module.id, "startDate", this.batchStartDate);
        }
        if (this.batchEndDate) {
          this.updateModuleDate(module.id, "endDate", this.batchEndDate);
        }
      });
    },
    validateDates(){
      for (const moduleId of this.changedModules) {
        const { startDate, endDate } = this.moduleDates[moduleId];
        if (new Date(startDate) > new Date(endDate)) {
          alert("End date must be after start date.");
          return false;
        }
        if (new Date(endDate) > new Date(this.survey.endDate))
        {
          alert("End date must be before survey end date.");
          return false;
        }
      }
      return true;
    },
    async saveDates() {
      this.loading = true;
      this.error = null;

      if (!this.validateDates()) {
        this.loading = false;
        return;
      }

      try {
        const changedDates = Array.from(this.changedModules).map(
          (moduleId) => ({
            surveyId: this.survey.id,
            moduleId,
            startDate: this.moduleDates[moduleId].startDate,
            endDate: this.moduleDates[moduleId].endDate,
          })
        );
        await this.updateSurveyModuleDates({
          surveyId: this.survey.id,
          moduleDates: changedDates,
        });
        this.$emit("close");
      } catch (error) {
        console.error("Error saving module dates:", error);
        this.error = "Failed to save module dates. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    formatDateForInput(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
      return date.toISOString().slice(0, 16); // Format: YYYY-MM-DDThh:mm
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleString();
    },
    toggleBatchUpdate() {
      this.showBatchUpdate = !this.showBatchUpdate;
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.fetchModuleDates();
        this.currentPage = 1;
        this.searchQuery = "";
        this.changedModules.clear();
        this.selectedModules = [];
        this.batchStartDate = "";
        this.batchEndDate = "";
        this.showBatchUpdate = false;
      }
    },
    searchQuery() {
      this.currentPage = 1;
    },
  },
  mounted() {
    if (this.show) {
      this.fetchModuleDates();
    }
  },
};
</script>